// Override of core useHotelAvailabilityMap (HotelAvailabilityMap/hooks.ts).
// Wraps the core hook and replaces map marker labels to use
// nightlyDiscountAwareTaxesAndFeesIncluded instead of nightlyDiscountAware.
//
// For packages: uses package prices from HotelListOverrideContext.priceExtractor
// instead of standalone hotel prices.
//
// RBC-1887: Disables "Search this area" button when map hasn't moved since last search
import { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useHotelAvailabilityMap as useHotelAvailabilityMapBase,
  type HotelAvailabilityMapProps,
  HotelsAvailabilitySelectors,
} from "@b2bportal/core-hotels";
import type { BoundingBox } from "@b2bportal/core-types";
import { toMapCoordinatesType } from "@b2bportal/core-utilities";
import type { LodgingPrice } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useHotelListOverride } from "@tenant/hotels";
import { useGetPriceDisplay } from "@tenant/ui";
import { PackageHotelSelectors } from "@/store/packages/selectors";

/**
 * Compares two bounding boxes for equality with tolerance for floating point precision
 * Returns true if both boxes have the same coordinates within tolerance, false otherwise
 */
const areBoundingBoxesEqual = (
  box1: BoundingBox | null | undefined,
  box2: BoundingBox | null | undefined,
  tolerance: number = 0.0001 // ~11 meters at equator
): boolean => {
  if (!box1 || !box2) return false;

  const withinTolerance = (a: number, b: number) => Math.abs(a - b) < tolerance;

  return (
    withinTolerance(box1.southWest.lat, box2.southWest.lat) &&
    withinTolerance(box1.southWest.lng, box2.southWest.lng) &&
    withinTolerance(box1.northEast.lat, box2.northEast.lat) &&
    withinTolerance(box1.northEast.lng, box2.northEast.lng)
  );
};

export const useHotelAvailabilityMap = (props: HotelAvailabilityMapProps) => {
  const { t } = useI18nContext();
  const override = useHotelListOverride();
  const base = useHotelAvailabilityMapBase(props);
  const { getPriceDisplay } = useGetPriceDisplay();
  const search = useSelector(HotelsAvailabilitySelectors.getSearch);
  const packageStateByLodgingId = useSelector(
    PackageHotelSelectors.getPackageStateByLodgingId
  );

  // RBC-1887: Track the last searched bounding box to prevent redundant searches
  const boundingBox = useSelector(
    HotelsAvailabilitySelectors.getMapBoundingBox
  );

  // Use a ref to track the bounding box when the last search was performed
  const lastSearchedBoundingBoxRef = useRef<BoundingBox | null>(null);

  // Determine if the map has moved since the last search
  // Button should be hidden when boxes are equal (map hasn't moved)
  const hasMapMoved = !areBoundingBoxesEqual(
    boundingBox,
    lastSearchedBoundingBoxRef.current
  );

  const points = useMemo(() => {
    // For packages: core filtered out all hotels because they don't have hotel.price
    // We need to recreate points from the original hotels list using package prices
    if (override?.priceExtractor && props.hotels.length > 0 && search != null) {
      // Safety check: verify package data is actually available by testing if ANY hotel has a price
      const hasAnyPrice = props.hotels.some(
        (h) => override.priceExtractor!(h) != null
      );

      if (!hasAnyPrice) {
        // Package data not loaded yet, fall back to core points
        return base.context.points;
      }

      const packagePoints = props.hotels
        .map((hotel) => {
          const packagePrice =
            packageStateByLodgingId?.[hotel.lodging.id]?.price?.totalPrice;

          const priceDisplay = packagePrice
            ? getPriceDisplay({
                prices: packagePrice,
              }).priceText
            : t("core-packages.hotelCardPrice.notAvailable");

          return {
            data: {
              lodging: hotel,
              dateRange: search.dateRange,
              rooms: search.rooms,
            },
            label: priceDisplay,
            id: hotel.lodging.id,
            coordinates: toMapCoordinatesType(
              hotel.lodging.location.coordinates
            ),
          };
        })
        .filter((point): point is NonNullable<typeof point> => point != null);

      // If we got no points from package prices, fall back to core points
      // This can happen during initial load before package data is fetched
      return packagePoints.length > 0 ? packagePoints : base.context.points;
    }

    // For standalone hotels: use nightlyDiscountAwareTaxesAndFeesIncluded
    return base.context.points.map((point) => {
      const price = (point.data.lodging as { price?: LodgingPrice }).price;
      if (price == null) return point;
      const taxesIncluded =
        price.nightlyDiscountAwareTaxesAndFeesIncluded ??
        price.nightlyDiscountAware;
      return {
        ...point,
        label: getPriceDisplay({
          prices: taxesIncluded.priceWithAllDiscounts,
        }).priceText,
      };
    });
  }, [
    base.context.points,
    override,
    props.hotels,
    getPriceDisplay,
    search,
    packageStateByLodgingId,
    t,
  ]);

  // RBC-1887: Wrap the search handler to track the searched bounding box
  const handleMapSearch = useCallback(() => {
    if (base.handlers.handleMapSearch && boundingBox) {
      // Store the current bounding box before performing the search
      lastSearchedBoundingBoxRef.current = boundingBox;
      // Call the original search handler
      base.handlers.handleMapSearch();
    }
  }, [base.handlers, boundingBox]);

  return useMemo(
    () => ({
      ...base,
      context: {
        ...base.context,
        points,
      },
      handlers: {
        ...base.handlers,
        // RBC-1887: Only enable search when map has moved
        // Set to undefined to hide the button when map hasn't moved
        handleMapSearch:
          hasMapMoved && base.handlers.handleMapSearch
            ? handleMapSearch
            : undefined,
      },
    }),
    [base, points, hasMapMoved, handleMapSearch]
  );
};
